/** A sandbox's **readiness scorecard** (client MCP plan §13.5, Q3; backend WPS P1b-08): the
 *  integration playbook's five "done" criteria over the sandbox's recent activity — pass or fail
 *  each, with evidence — the scorecard its developer gets from the MCP tool `check_readiness`.
 *  Passing never unlocks production: a person does.
 *
 *  `GET /tour_operators/{top_id}/integration_readiness/?days=` (`console_integration_readiness_view`,
 *  Ops Admin by default): `days` 1–14 (default 14, what a sandbox keeps). The activity is the
 *  documents its developer sent, read newest first within a time budget and caps (`partial` when
 *  some were not read), their import reports, the change history they wrote, the bookings the
 *  sandbox holds and its request log. Never the importer's messages, a host or a key; contacts in
 *  a difference are masked, values quoted at most 80 characters. 404: no such tenant; 409
 *  `not_sandbox`; 400 `statement_timeout` (ask for fewer days, or the extended budget).
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiGet, extendedListConfig } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type IntegrationReadiness = Schemas['IntegrationReadiness'];
export type ReadinessCriterion = Schemas['IntegrationReadinessCriterion'];
export type ReadinessEvidence = Schemas['IntegrationReadinessEvidence'];
export type ReadinessCriterionCode = ReadinessCriterion['code'];
export type ReadinessReason = ReadinessCriterion['reason'];
export type ReadinessScenario = NonNullable<ReadinessEvidence['scenario']>;

export const MAX_DAYS = 14;
/** The windows the tab offers (days of activity, up to now). */
export const DAY_OPTIONS = [1, 3, 7, 14] as const;

export function readiness(
  topId: number,
  { days, timeout }: { days?: number; timeout?: 'extended' } = {},
): Promise<IntegrationReadiness> {
  const usp = new URLSearchParams();
  if (days !== undefined && days !== MAX_DAYS) usp.set('days', String(days));
  if (timeout) usp.set('timeout', timeout);
  const qs = usp.toString();
  return apiGet(
    `/tour_operators/${topId}/integration_readiness/${qs ? `?${qs}` : ''}`,
    extendedListConfig(timeout),
  );
}

export const readinessKeys = {
  all: ['integrationReadiness'] as const,
  scorecard: (topId: number, days: number, timeout?: 'extended') =>
    ['integrationReadiness', topId, days, timeout ?? null] as const,
};

// ---- words -----------------------------------------------------------------------

/** The criteria in the playbook's order (the contract test keeps it equal to the spec's enum). */
export const CRITERIA = [
  'scenarios_imported',
  'read_back_matches',
  'resend_changes_nothing',
  'out_of_order_kept',
  'backs_off',
] as const satisfies readonly ReadinessCriterionCode[];

export const REASON_LABELS: Record<ReadinessReason, string> = {
  passed: 'Passed',
  no_activity: 'No activity yet',
  not_shown: 'Not shown yet',
  violated: 'Violated',
};

export const SCENARIO_LABELS: Record<ReadinessScenario, string> = {
  create: 'Create',
  change: 'Change',
  reschedule: 'Reschedule',
  cancel: 'Cancel',
  multi_tour_order: 'One order on two tours',
};

/** Why a finished import's document was not read (`activity.documents_unread`). */
export const UNREAD_LABELS: Record<string, string> = {
  expired: 'no longer stored',
  too_large: 'too large',
  not_stored: 'not stored',
  unreadable: 'unreadable',
  over_cap: 'past the newest 60',
  over_budget: 'past the time budget',
};

/** "12 resends" — a criterion's count key in words. */
export function countLabel(key: string): string {
  return key.replace(/_/g, ' ');
}
